import { usePostHog } from 'posthog-js/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useCaptureError } from '@/lib/posthog/hooks/useCaptureError';
import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import { CompanyProfileCheck } from '@/modules/companies/components/companyProfileCheck/CompanyProfileCheck';
import { useUpdateCompanyMutation } from '@/modules/companies/mutations/useUpdateCompanyMutation';
import { useCompanyQuery } from '@/modules/companies/queries/useCompanyQuery';
import { StepHeader } from '@/modules/signup-flow/components/layout/StepHeader';
import { useUpdateSignupFlowMutation } from '@/modules/signup-flow/mutations/useUpdateSignupFlowMutation';
import {
  SIGN_UP_FLOW_STEPS,
  signupFlowStep,
} from '@/modules/signup-flow/signupFlowSteps';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';
import { isLocalEnv } from '@/utils/env';

import {
  type CompanyProfile,
  CompanyProfileForm,
  DEFAULT_COMPANY_SIZE,
} from './CompanyProfileForm';

/**
 * The form reads its initial values once on mount, so it waits for the
 * company query; otherwise the pre-filled fields would stay empty.
 */
const CompanyProfileSkeleton = () => (
  <div
    className="flex flex-col gap-8"
    aria-busy="true"
    aria-label="Loading company profile"
  >
    <div className="flex flex-col gap-3">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-8 w-3/4" />
      <Skeleton className="h-4 w-2/3" />
    </div>
    <div className="flex flex-col gap-5">
      {['name', 'website'].map((field) => (
        <div key={field} className="flex flex-col gap-1.5">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-full" />
        </div>
      ))}
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-44 w-full" />
      </div>
    </div>
  </div>
);

/**
 * Step 1: company profile. Persists the company, asks "Is this your company?" of what Coresignal has for its website,
 * then moves on to the next step.
 */
export const AboutCompany = () => {
  const captureError = useCaptureError();
  const trackEvent = useTrackEvent();
  const posthog = usePostHog();

  const currentUserQuery = useCurrentUserQuery();
  const companyId = currentUserQuery.data?.defaultTenantId ?? '';
  const companyQuery = useCompanyQuery(companyId, {
    enabled: Boolean(companyId),
  });

  const updateCompanyMutation = useUpdateCompanyMutation();
  const updateSignupFlowMutation = useUpdateSignupFlowMutation();
  // The profile once it's saved, while "Is this your company?" is asked.
  const [saved, setSaved] = useState<CompanyProfile>();

  const onContinue = async (profile: CompanyProfile) => {
    const clientStatus = companyQuery.data?.clientStatus;

    if (!clientStatus) {
      toast.error(
        'Failed to load your company profile. Please refresh and try again.',
      );
      return;
    }

    try {
      await updateCompanyMutation.mutateAsync({
        id: companyId,
        description: '',
        ownerIds: [],
        clientStatus,
        name: profile.name,
        websiteUrl: profile.websiteUrl,
        location: profile.location,
      });

      // The size is kept now; the step moves on once "Is this your company?" is answered.
      await updateSignupFlowMutation.mutateAsync({
        state: { companySize: profile.size },
      });
      // On the person as soon as it's saved, so campaign funnels can break
      // down by company even when the user stops at "Is this your company?".
      if (!isLocalEnv) {
        posthog.setPersonProperties({
          companyName: profile.name,
          companySize: profile.size,
        });
      }
      setSaved(profile);
    } catch (error) {
      captureError(error, { errorType: 'about-company-signup-flow-error' });
      toast.error(
        'Failed to set up your company profile. Please try again or contact support.',
      );
    }
  };

  const onChecked = async (profile: CompanyProfile) => {
    try {
      await updateSignupFlowMutation.mutateAsync({
        step: signupFlowStep.companyGoals,
        state: { companySize: profile.size },
      });

      trackEvent('aboutCompanyFinished', {
        companyName: profile.name,
        companySize: profile.size,
        companyHeadquarters: profile.location,
      });
    } catch (error) {
      captureError(error, { errorType: 'about-company-signup-flow-error' });
      toast.error(
        'Failed to set up your company profile. Please try again or contact support.',
      );
    }
  };

  if (currentUserQuery.isPending || companyQuery.isPending) {
    return <CompanyProfileSkeleton />;
  }

  if (saved) {
    return (
      <div className="flex flex-col gap-8">
        <StepHeader
          step={signupFlowStep.aboutCompany}
          totalSteps={SIGN_UP_FLOW_STEPS}
          title="One more thing about your company"
          subtitle="Finlay suggests what size and industry of company your jobs find people from, starting with your own."
        />
        <CompanyProfileCheck
          companyId={companyId}
          signUpSize={saved.size}
          onDone={() => void onChecked(saved)}
        />
      </div>
    );
  }

  const company = companyQuery.data;

  return (
    <CompanyProfileForm
      firstName={currentUserQuery.data?.firstName || 'there'}
      initial={{
        name: company?.name ?? '',
        websiteUrl: company?.websiteUrl ?? '',
        size: company?.onboarding?.state?.companySize || DEFAULT_COMPANY_SIZE,
        location: company?.location ?? '',
      }}
      isSubmitting={
        updateCompanyMutation.isPending || updateSignupFlowMutation.isPending
      }
      onContinue={onContinue}
    />
  );
};
